import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import PrimaryButton from '../components/PrimaryButton';
import parashaList from '../../parasha_map.json';
import { getParasha, setParasha } from '../utils/userSelections';
import { syncSnapshotToFirestore } from '../utils/progressStorage';
import { trackPageView, trackSelection, trackNavigation, trackClick } from '../utils/analytics';

// Color constants (matching AppOnboardingPage theme)
const BLUE_ACCENT = '#3B7BF4';
const GOLD_BUTTON = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

type Parasha = { english: string; hebrew: string };

const parashaData = parashaList as Parasha[];

// Group parashot by Torah book (same indexing used in BirthDatePage)
const parashotByBook: Array<{
  key: string;
  labelEn: string;
  labelHe: string;
  parashot: Parasha[];
}> = [
  {
    key: 'bereshit',
    labelEn: 'Bereshit',
    labelHe: 'בראשית',
    parashot: parashaData.slice(0, 12),
  },
  {
    key: 'shemot',
    labelEn: 'Shemot',
    labelHe: 'שמות',
    parashot: parashaData.slice(12, 23),
  },
  {
    key: 'vayikra',
    labelEn: 'Vayikra',
    labelHe: 'ויקרא',
    parashot: parashaData.slice(23, 33),
  },
  {
    key: 'bamidbar',
    labelEn: 'Bamidbar',
    labelHe: 'במדבר',
    parashot: parashaData.slice(33, 43),
  },
  {
    key: 'devarim',
    labelEn: 'Devarim',
    labelHe: 'דברים',
    parashot: parashaData.slice(43),
  },
];

const ChooseParshaPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [activeBook, setActiveBook] = useState<string>(parashotByBook[0].key);
  const [selectedParasha, setSelectedParasha] = useState<Parasha | null>(() => getParasha());

  useEffect(() => {
    trackPageView('ChooseParsha');
  }, []);

  const activeParashot = useMemo(() => {
    const book = parashotByBook.find((b) => b.key === activeBook);
    return book?.parashot ?? parashaData;
  }, [activeBook]);

  const filteredParashot = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return activeParashot;
    const lower = trimmed.toLowerCase();
    return parashaData.filter(
      (p) => p.english.toLowerCase().includes(lower) || p.hebrew.includes(trimmed),
    );
  }, [activeParashot, query]);

  const weeklyParasha = useMemo(() => {
    if (selectedParasha) return selectedParasha;
    if (filteredParashot.length) return filteredParashot[0];
    return activeParashot[0];
  }, [activeParashot, filteredParashot, selectedParasha]);

  const handleSelect = (p: Parasha) => {
    setSelectedParasha(p);
    setParasha({ english: p.english, hebrew: p.hebrew });
    void syncSnapshotToFirestore({ english: p.english, hebrew: p.hebrew });
    trackSelection('parasha_card', p.english, { hebrew: p.hebrew });
    trackNavigation('ChooseParsha', 'Practice', 'button');
    navigate('/practice');
  };

  const handleLearnNow = () => {
    if (!weeklyParasha) return;
    handleSelect(weeklyParasha);
    trackClick('LearnNow', 'ChooseParsha');
  };

  return (
    <div
      className="flex flex-col flex-grow bg-white"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Main scrollable content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: '#F6F7FB',
          minHeight: 0, // important for children overflow
        }}
      >
        {/* Hero banner */}

        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'white',
            borderRadius: 18,
            padding: '12px 14px',
            border: `1px solid ${BORDER_LIGHT}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            direction: isHebrew ? 'rtl' : 'ltr',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={TEXT_SECONDARY}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={isHebrew ? { transform: 'scaleX(-1)' } : undefined}
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              t('search_parasha_placeholder') ||
              (isHebrew ? 'חפש פרשה...' : 'Search for a parasha...')
            }
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              flex: 1,
              fontSize: 14,
              color: TEXT_DARK,
              direction: isHebrew ? 'rtl' : 'ltr',
            }}
            aria-label={t('search_parasha_aria') || (isHebrew ? 'חיפוש פרשה' : 'Search parasha')}
          />
        </div>

        {/* Weekly highlight or Add Parasha prompt */}
        {selectedParasha ? (
          <div
            style={{
              borderRadius: 20,
              padding: '12px 14px',
              background: BLUE_ACCENT,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 20px rgba(59, 123, 244, 0.25)',
              direction: isHebrew ? 'rtl' : 'ltr',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 80% 10%, rgba(255,255,255,0.15), transparent 35%), radial-gradient(circle at 10% 20%, rgba(255,255,255,0.08), transparent 30%)',
              }}
            />
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    marginBottom: 2,
                    lineHeight: 1.2,
                    direction: isHebrew ? 'rtl' : 'ltr',
                  }}
                >
                  ✨ {isHebrew ? selectedParasha.hebrew : selectedParasha.english}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.85)',
                    lineHeight: 1.4,
                    direction: isHebrew ? 'rtl' : 'ltr',
                    textAlign: isHebrew ? 'right' : 'left',
                  }}
                >
                  {t('weekly_portion_tagline') ||
                    (isHebrew
                      ? 'התחל/י ללמוד את פרשת השבוע'
                      : "Start learning this week's parasha")}
                </p>
              </div>
              <PrimaryButton
                onClick={handleLearnNow}
                text={t('learn_now') || (isHebrew ? 'למד' : 'Learn')}
                variant="gold"
                size="sm"
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  background: GOLD_BUTTON,
                  color: TEXT_DARK,
                }}
                fullWidth={false}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              borderRadius: 20,
              padding: '20px 16px',
              background: 'linear-gradient(135deg, #F8F9FC 0%, #EEF1F6 100%)',
              border: `2px dashed ${BORDER_LIGHT}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              direction: isHebrew ? 'rtl' : 'ltr',
            }}
            onClick={() => {
              const listSection = document.querySelector('[data-parasha-list]');
              listSection?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: TEXT_DARK,
                  marginBottom: 2,
                }}
              >
                {t('add_parasha_title') || (isHebrew ? 'בחר/י פרשה' : 'Choose a Parasha')}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: TEXT_SECONDARY,
                }}
              >
                {t('add_parasha_subtitle') ||
                  (isHebrew ? 'בחר/י מהרשימה למטה' : 'Tap here or select from the list below')}
              </div>
            </div>
          </div>
        )}

        {/* Books filter */}
        <div
          style={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            paddingBottom: 4,
            flexDirection: !isHebrew ? 'row' : 'row-reverse',
            // direction: isHebrew ? "rtl" : "ltr",
          }}
        >
          {parashotByBook.map((book) => {
            const isActive = book.key === activeBook && !query.trim();
            return (
              <button
                key={book.key}
                onClick={() => setActiveBook(book.key)}
                style={{
                  borderRadius: 0,
                  padding: '1px 14px',
                  border: isActive ? `1.5px solid ${BLUE_ACCENT}` : `1px solid ${BORDER_LIGHT}`,
                  background: isActive ? BLUE_ACCENT : 'white',
                  color: isActive ? '#FFFFFF' : TEXT_DARK,
                  fontWeight: 600,
                  fontSize: 13,
                  minWidth: 52,
                  cursor: 'pointer',
                  boxShadow: isActive
                    ? '0 6px 18px rgba(59, 123, 244, 0.25)'
                    : '0 2px 8px rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  direction: isHebrew ? 'rtl' : 'ltr',
                }}
                aria-pressed={isActive}
                aria-label={t(`book_label_${book.key}`) || (isHebrew ? book.labelHe : book.labelEn)}
              >
                {t(`book_label_${book.key}`) || (isHebrew ? book.labelHe : book.labelEn)}
              </button>
            );
          })}
        </div>

        {/* Parasha list, scrolls below */}
        <div
          data-parasha-list
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            direction: isHebrew ? 'rtl' : 'ltr',
            flex: 1,
            overflow: 'auto', // ensures scrolling for large lists
            minHeight: 0,
            marginBottom: 4,
          }}
        >
          {filteredParashot.map((p) => {
            const isSelected = selectedParasha?.english.toLowerCase() === p.english.toLowerCase();
            return (
              <button
                key={p.english}
                onClick={() => handleSelect(p)}
                style={{
                  width: '100%',
                  direction: isHebrew ? 'rtl' : 'ltr',
                  background: 'white',
                  borderRadius: 0,
                  padding: '10px 12px',
                  border: isSelected ? `1.5px solid ${BLUE_ACCENT}` : `1px solid ${BORDER_LIGHT}`,
                  boxShadow: isSelected
                    ? '0 10px 24px rgba(59, 123, 244, 0.2)'
                    : '0 6px 16px rgba(0, 0, 0, 0.06)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  transition: 'all 0.2s ease',
                }}
                aria-pressed={isSelected}
                aria-label={
                  t('choose_this_parasha', {
                    name: isHebrew ? p.hebrew : p.english,
                  }) || (isHebrew ? `בחר/י פרשה ${p.hebrew}` : `Choose ${p.english} Parasha`)
                }
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: TEXT_DARK,
                    }}
                  >
                    {isHebrew ? p.hebrew : p.english}
                  </span>
                </div>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isSelected ? BLUE_ACCENT : 'rgba(59, 123, 244, 0.08)',
                    color: isSelected ? '#FFFFFF' : BLUE_ACCENT,
                    border: isSelected ? 'none' : `1px solid ${BORDER_LIGHT}`,
                    boxShadow: isSelected ? '0 6px 14px rgba(59, 123, 244, 0.3)' : 'none',
                    fontWeight: 700,
                    borderRadius: 8,
                  }}
                  aria-hidden
                >
                  {isSelected ? (
                    '✓'
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isHebrew ? 'rotate(180deg)' : 'none',
                      }}
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}

          {!filteredParashot.length && (
            <div
              style={{
                textAlign: 'center',
                color: TEXT_SECONDARY,
                padding: '24px 12px',
                background: 'white',
                border: `1px solid ${BORDER_LIGHT}`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              }}
            >
              {t('no_results') || (isHebrew ? 'לא נמצאו תוצאות' : 'No results found')}
            </div>
          )}
        </div>
      </div>

      {/* Fixed footer */}

      <BottomNav />
    </div>
  );
};

export default ChooseParshaPage;
